import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check, X, ShieldAlert } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { LineReveal } from "@/components/LineReveal";
import { LeadForm } from "@/components/LeadForm";
import { getService } from "@/data/services";
import { packagesForService, formatPrice } from "@/data/packages";
import NotFound from "@/pages/NotFound";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <NotFound />;

  const origin = window.location.origin;
  const packages = packagesForService(service.slug);

  return (
    <main data-testid={`service-detail-${service.slug}`}>
      <Seo
        title={service.metaTitle}
        siteName="Electra HR"
        description={service.metaDescription}
        image={`/api/og/service-${service.slug}.jpg`}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              name: service.title,
              provider: { "@id": `${origin}/#organization` },
              areaServed: { "@type": "Country", name: "United Kingdom" },
              serviceType: "HR Consultancy",
              description: service.metaDescription,
              url: `${origin}/services/${service.slug}`,
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: origin },
                { "@type": "ListItem", position: 2, name: "Services", item: `${origin}/services` },
                { "@type": "ListItem", position: 3, name: service.title, item: `${origin}/services/${service.slug}` },
              ],
            },
          ],
        }}
      />

      <section className="grain bg-ink pb-20 pt-32 sm:pt-40 text-paper">
        <div className="container-x">
          <nav aria-label="Breadcrumb" className="font-mono text-xs uppercase tracking-[0.2em] text-paper/40">
            <Link to="/services" data-testid="breadcrumb-services" className="hover:text-pink transition-colors">Services</Link>
            <span className="mx-2 text-pink">/</span> {service.shortTitle}
          </nav>
          <LineReveal
            className="mt-6 max-w-4xl font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]"
            lines={[service.title]}
            delay={0.1}
          />
          <p className="mt-5 max-w-2xl font-display text-lg sm:text-xl text-pink">{service.tagline}</p>
          <p className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed text-paper/65">{service.problem}</p>
        </div>
      </section>

      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Reveal>
            <div data-testid="service-included-panel" className="h-full border-2 border-ink p-8 sm:p-10">
              <p className="eyebrow text-ink/50">What's included</p>
              <ul className="mt-6 space-y-4">
                {service.included.map((item) => (
                  <li key={item} className="flex gap-3 text-sm sm:text-base leading-relaxed">
                    <Check size={20} className="mt-0.5 shrink-0 text-pink" strokeWidth={3} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="service-retainer-gap-panel" className="h-full border border-ink/15 bg-ink/[0.03] p-8 sm:p-10">
              <p className="eyebrow text-ink/50">Where retainer firms fall short</p>
              <ul className="mt-6 space-y-4">
                {service.retainerGap.map((item) => (
                  <li key={item} className="flex gap-3 text-sm sm:text-base leading-relaxed text-ink/60">
                    <X size={20} className="mt-0.5 shrink-0 text-signal" strokeWidth={2.5} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {packages.length > 0 && (
        <section data-testid="service-prices" className="bg-paper pb-16 sm:pb-24">
          <div className="container-x">
            <Reveal>
              <p className="eyebrow text-ink/50">Fixed prices</p>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                You know the price before any work starts.
              </h2>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              {packages.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.08}>
                  <div data-testid={`service-package-${p.id}`} className="flex h-full flex-col border-2 border-ink p-7 sm:p-8">
                    <p className="font-display text-xl font-semibold">{p.name}</p>
                    <p className="mt-2 text-sm leading-relaxed text-ink/60">{p.detail}</p>
                    <p className="mt-5 font-display text-2xl font-semibold text-ink">{formatPrice(p)}</p>
                    <p className="mt-1 font-mono text-xs uppercase tracking-[0.18em] text-ink/45">{p.turnaround}</p>
                    <a
                      href="#enquire"
                      data-testid={`service-package-cta-${p.id}`}
                      onClick={() => window.dispatchEvent(new CustomEvent("electra:package", { detail: p.id }))}
                      className="group mt-auto pt-6 inline-flex items-center gap-2 font-bold text-ink"
                    >
                      Get started <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </a>
                  </div>
                </Reveal>
              ))}
            </div>
            <p className="mt-6 text-sm text-ink/55">
              Something bigger or ongoing? Buy a <Link to="/services#pricing" className="font-semibold underline decoration-pink decoration-2 underline-offset-4">block of hours</Link> to use within 12 months. No monthly fee.
            </p>
          </div>
        </section>
      )}

      <section className="bg-ink py-16 sm:py-24 text-paper">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow text-pink">How it runs</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
              The process, in four moves
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {service.steps.map((step, i) => (
              <Reveal key={step.title} delay={i * 0.08} className="bg-ink">
                <div data-testid={`service-step-${i + 1}`} className="h-full p-7 sm:p-8">
                  <p className="font-mono text-sm text-pink">0{i + 1}</p>
                  <h3 className="mt-3 font-display text-lg font-semibold leading-snug">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-paper/55">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div data-testid="bonsai-escalation-note" className="mt-8 flex gap-4 border border-pink/30 bg-ink-soft p-6 sm:p-7">
              <ShieldAlert size={26} className="shrink-0 text-pink" />
              <p className="text-sm leading-relaxed text-paper/65">
                If this matter turns legal, for example a claim, a contentious dismissal or a discrimination allegation,
                we bring in Bonsai Law, an SRA-regulated law firm, with your consent and full context handed over.
                You never pay lawyer rates for HR work, or HR rates for legal work.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="enquire" className="bg-pink py-16 sm:py-24 text-ink">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-12">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.05]">
              Get a fixed quote for {service.shortTitle.toLowerCase()}.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink/70">
              A fixed price before any work starts, from an HR adviser within one working day. By email, or a call with the adviser if you prefer.
            </p>
            <Link to="/services" data-testid="service-back-link" className="group mt-8 inline-flex items-center gap-2 font-bold">
              All services <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border-2 border-ink bg-paper p-7 sm:p-9 shadow-[10px_10px_0_0_#0A0E17]">
              <LeadForm key={service.slug} source={`service:${service.slug}`} defaultPackage={packages.length === 1 ? packages[0].id : ""} />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
